import { Injectable, inject, signal } from '@angular/core';
import { MessageService } from '@openng/optimus-ui/api';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { GridResponse } from '../../shared/models/common/grid-response.model';
import { BackgroundJobMessage } from '../../shared/models/jobs/background-job.model';
import { EntityCounterMessage } from '../../shared/models/notifications/entity-counter.model';
import { NotificationPreference, UserNotification } from '../../shared/models/notifications/user-notification.model';
import { resolveNotificationMessage, resolveNotificationTitle } from '../../shared/models/notifications/notification-text';

/** Notifications kept in memory for the bell dropdown. */
const BELL_PAGE_SIZE = 25;

@Injectable({ providedIn: 'root' })
export class UserNotificationsService {
    private http = inject(HttpClient);
    private readonly translate = inject(TranslateService);
    private readonly messageService = inject(MessageService);
    private readonly router = inject(Router);

    // SignalR connection
    private hubConnection: signalR.HubConnection | null = null;

    // State signals
    notifications = signal<UserNotification[]>([]);
    unreadCount = signal<number>(0);
    isConnected = signal<boolean>(false);

    // Trigger for new notification arrival (for bell shake animation)
    newNotification$ = new Subject<UserNotification>();

    /** Catalogue codes the user wants as browser notices. */
    private browserEnabledTypes = new Set<string>();

    /** Progress of background jobs, pushed over the same hub connection. */
    jobProgress$ = new Subject<BackgroundJobMessage>();

    /**
     * Live entity counters (e.g. an event space's check-in count), pushed over the same hub
     * connection and the same `entity-{companyUid}-{entityType}-{entityId}` groups that
     * `subscribeEntity`/`unsubscribeEntity` already join for background jobs.
     */
    entityCounter$ = new Subject<EntityCounterMessage>();

    /** Groups re-joined after a reconnect, since SignalR forgets them. */
    private watchedJobs = new Set<string>();
    private watchedJobEntities = new Set<string>();

    /**
     * Connect to SignalR notifications hub
     */
    async connect(): Promise<void> {
        if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
            return;
        }

        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
            console.warn('[UserNotificationsService] No access token found, skipping SignalR connection');
            return;
        }

        // Build hub connection with automatic reconnect
        // Note: Hub path is /hubs/notifications (not /api/hubs/notifications)
        // Proxy config handles /hubs route for local development
        this.hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(`${window.location.origin}/hubs/notifications`, {
                accessTokenFactory: () => localStorage.getItem('accessToken') || ''
            })
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000]) // Exponential backoff
            .build();

        // Register event handlers
        this.registerEventHandlers();

        try {
            await this.hubConnection.start();
            this.isConnected.set(true);
            console.log('[UserNotificationsService] SignalR connected');

            // Load initial data after connection
            this.loadNotifications();
            this.loadUnreadCount();
            this.loadBrowserPreferences();
            this.rejoinWatchedGroups();
        } catch (err) {
            console.error('[UserNotificationsService] SignalR connection error:', err);
            this.isConnected.set(false);
        }

        // Handle reconnection
        this.hubConnection.onreconnected(() => {
            console.log('[UserNotificationsService] SignalR reconnected');
            this.isConnected.set(true);
            // A reconnect may have missed pushes; the server state wins.
            this.loadNotifications();
            this.loadUnreadCount();
            // Group membership does not survive a reconnect: a progress bar would go
            // quiet for the rest of the job without this.
            this.rejoinWatchedGroups();
        });

        this.hubConnection.onreconnecting(() => {
            console.log('[UserNotificationsService] SignalR reconnecting...');
            this.isConnected.set(false);
        });

        this.hubConnection.onclose(() => {
            console.log('[UserNotificationsService] SignalR connection closed');
            this.isConnected.set(false);
        });
    }

    /**
     * Disconnect from SignalR hub
     */
    async disconnect(): Promise<void> {
        if (this.hubConnection) {
            try {
                await this.hubConnection.stop();
                this.isConnected.set(false);
                console.log('[UserNotificationsService] SignalR disconnected');
            } catch (err) {
                console.error('[UserNotificationsService] Error disconnecting:', err);
            }
        }
    }

    /**
     * Load the most recent notifications of the current user into the bell.
     */
    loadNotifications(): void {
        this.query({ pageSize: BELL_PAGE_SIZE }).subscribe({
            next: (response) => {
                this.notifications.set(response.rows ?? []);
            },
            error: (err) => {
                console.error('[UserNotificationsService] Error loading notifications:', err);
            }
        });
    }

    /**
     * Paged query for the notifications inbox. The bell uses the same endpoint with its
     * own page size, so both views always agree on what exists.
     */
    query(options: { unreadOnly?: boolean; category?: string | null; page?: number; pageSize?: number } = {}): Observable<GridResponse<UserNotification>> {
        const params: Record<string, string> = {
            page: String(options.page ?? 1),
            pageSize: String(options.pageSize ?? BELL_PAGE_SIZE)
        };

        if (options.unreadOnly) {
            params['unreadOnly'] = 'true';
        }

        if (options.category) {
            params['category'] = options.category;
        }

        return this.http.get<GridResponse<UserNotification>>(
            `${environment.config.api_url}usernotifications`,
            { params }
        );
    }

    /**
     * Load unread count
     */
    loadUnreadCount(): void {
        this.http.get<{ count: number }>(`${environment.config.api_url}usernotifications/unread-count`)
            .subscribe({
                next: (response) => {
                    this.unreadCount.set(response.count);
                },
                error: (err) => {
                    console.error('[UserNotificationsService] Error loading unread count:', err);
                }
            });
    }

    /**
     * Mark a notification as read
     */
    markAsRead(id: number): void {
        const target = this.notifications().find((n) => n.id === id);
        if (target?.is_read) {
            return;
        }

        this.http.post(`${environment.config.api_url}usernotifications/${id}/read`, {})
            .subscribe({
                next: () => {
                    this.applyRead(id);
                },
                error: (err) => {
                    console.error('[UserNotificationsService] Error marking notification as read:', err);
                }
            });
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void {
        this.http.post(`${environment.config.api_url}usernotifications/read-all`, {})
            .subscribe({
                next: () => {
                    this.applyAllRead();
                },
                error: (err) => {
                    console.error('[UserNotificationsService] Error marking all as read:', err);
                }
            });
    }

    /**
     * Follows one background job's progress. Safe to call before the connection is up:
     * the group is remembered and joined on connect.
     */
    async subscribeJob(jobUid: string): Promise<void> {
        this.watchedJobs.add(jobUid);
        await this.invokeSafely('SubscribeJob', jobUid);
    }

    /** Stops following one job. */
    async unsubscribeJob(jobUid: string): Promise<void> {
        this.watchedJobs.delete(jobUid);
        await this.invokeSafely('UnsubscribeJob', jobUid);
    }

    /** Follows every job of one entity — an event's imports, say. */
    async subscribeEntity(entityType: string, entityId: number): Promise<void> {
        this.watchedJobEntities.add(`${entityType}:${entityId}`);
        await this.invokeSafely('SubscribeEntity', entityType, entityId);
    }

    /** Stops following one entity's jobs. */
    async unsubscribeEntity(entityType: string, entityId: number): Promise<void> {
        this.watchedJobEntities.delete(`${entityType}:${entityId}`);
        await this.invokeSafely('UnsubscribeEntity', entityType, entityId);
    }

    private rejoinWatchedGroups(): void {
        for (const jobUid of this.watchedJobs) {
            void this.invokeSafely('SubscribeJob', jobUid);
        }

        for (const key of this.watchedJobEntities) {
            const [entityType, entityId] = key.split(':');
            void this.invokeSafely('SubscribeEntity', entityType, Number(entityId));
        }
    }

    /**
     * Calls a hub method when there is a connection. A missed join is not fatal: the job
     * is in the database and the screen can still poll it.
     */
    private async invokeSafely(method: string, ...args: unknown[]): Promise<void> {
        if (this.hubConnection?.state !== signalR.HubConnectionState.Connected) {
            return;
        }

        try {
            await this.hubConnection.invoke(method, ...args);
        } catch (err) {
            console.error(`[UserNotificationsService] Hub call ${method} failed:`, err);
        }
    }

    /**
     * Caches which types the user silenced for the browser. Loaded once per connection:
     * the alternative is asking the server on every arriving notification.
     */
    private loadBrowserPreferences(): void {
        this.getPreferences().subscribe({
            next: (preferences) => {
                this.browserEnabledTypes = new Set(
                    (preferences ?? [])
                        .filter(p => p.browser_eligible && p.browser_enabled)
                        .map(p => p.code)
                );
            },
            error: (err) => {
                // Without preferences no browser notice is raised: the choice is the user's,
                // and guessing "yes" would be the intrusive guess.
                console.error('[UserNotificationsService] Error loading browser preferences:', err);
            }
        });
    }

    /**
     * Raises a system notification when the tab is in the background. With the tab in
     * front the bell is already visible, and a second alert for the same thing is noise.
     *
     * Nothing happens without permission: it is requested from the preferences screen,
     * inside a click, because browsers reject — and sometimes permanently block — a
     * prompt raised on page load.
     */
    private maybeShowBrowserNotification(notification: UserNotification): void {
        if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
            return;
        }

        // With the tab in front the bell is already visible and a toast has just fired; a
        // system notice on top of that is noise.
        if (document.visibilityState === 'visible') {
            return;
        }

        if (!notification.browser_eligible) {
            return;
        }

        if (!notification.type_code || !this.browserEnabledTypes.has(notification.type_code)) {
            return;
        }

        try {
            const notice = new Notification(resolveNotificationTitle(notification, this.translate), {
                body: resolveNotificationMessage(notification, this.translate),
                // Tagged by group: an update to a grouped notification replaces its own
                // notice instead of stacking one per occurrence.
                tag: notification.group_key ?? `stela-notification-${notification.id}`,
                icon: notification.icon ?? undefined
            });

            notice.onclick = () => {
                window.focus();
                notice.close();

                if (notification.route) {
                    void this.router.navigateByUrl(notification.route);
                }
            };
        } catch (err) {
            console.error('[UserNotificationsService] Error showing browser notification:', err);
        }
    }

    /**
     * Toast for a notification that arrives with the tab in front. It is the in-app echo of
     * what the browser notice does when the tab is hidden; one or the other, never both.
     */
    private maybeShowToast(notification: UserNotification): void {
        if (document.visibilityState !== 'visible') {
            return;
        }

        this.messageService.add({
            severity: notification.severity === 'error' ? 'error' : notification.severity ?? 'info',
            summary: resolveNotificationTitle(notification, this.translate),
            detail: resolveNotificationMessage(notification, this.translate),
            life: 5000,
            data: { route: notification.route }
        });
    }

    /**
     * Notification catalogue with the current user's choices resolved.
     */
    getPreferences(): Observable<NotificationPreference[]> {
        return this.http.get<NotificationPreference[]>(`${environment.config.api_url}usernotifications/preferences`);
    }

    /**
     * Store the current user's choices. Mandatory types are ignored server-side.
     */
    savePreferences(preferences: NotificationPreference[]): Observable<NotificationPreference[]> {
        return this.http.put<NotificationPreference[]>(
            `${environment.config.api_url}usernotifications/preferences`,
            preferences
        ).pipe(
            tap((saved) => {
                // Saving from the preferences screen has to take effect without a reload.
                this.browserEnabledTypes = new Set(
                    (saved ?? [])
                        .filter(p => p.browser_eligible && p.browser_enabled)
                        .map(p => p.code)
                );
            })
        );
    }

    /**
     * Register SignalR event handlers
     */
    private registerEventHandlers(): void {
        if (!this.hubConnection) return;

        // New notification received
        this.hubConnection.on('ReceiveNotification', (notification: UserNotification) => {
            this.upsert(notification, true);
        });

        // A grouped notification gained occurrences: it is the same row, not a new one.
        this.hubConnection.on('NotificationUpdated', (notification: UserNotification) => {
            this.upsert(notification, false);
        });

        // Notification marked as read (by another session/device)
        this.hubConnection.on('NotificationRead', (data: { notificationId: number }) => {
            this.applyRead(data.notificationId);
        });

        // All notifications marked as read (by another session/device)
        this.hubConnection.on('AllNotificationsRead', () => {
            this.applyAllRead();
        });

        // Unread count changed (sync from server)
        this.hubConnection.on('UnreadCountChanged', (data: { count: number }) => {
            this.unreadCount.set(data.count);
        });

        // Background job progress and status changes.
        this.hubConnection.on('JobProgress', (message: BackgroundJobMessage) => {
            this.jobProgress$.next(message);
        });

        // Live entity counters (e.g. an event space's check-in count).
        this.hubConnection.on('EntityCounter', (message: EntityCounterMessage) => {
            this.entityCounter$.next(message);
        });
    }

    /**
     * Puts an incoming notification at the top of the list, replacing the row when it is
     * one we already hold — which is what happens every time a grouped notification gains
     * an occurrence, and also when a push is redelivered by the outbox.
     */
    private upsert(notification: UserNotification, isNew: boolean): void {
        const current = this.notifications();
        const existingIndex = current.findIndex((n) => n.id === notification.id);
        const wasUnread = existingIndex >= 0 && !current[existingIndex].is_read;

        const withoutExisting = existingIndex >= 0
            ? [...current.slice(0, existingIndex), ...current.slice(existingIndex + 1)]
            : current;

        this.notifications.set([notification, ...withoutExisting].slice(0, BELL_PAGE_SIZE));

        // The server sends the authoritative count right after; this only keeps the badge
        // honest in the gap between both pushes.
        if (!notification.is_read && !wasUnread) {
            this.unreadCount.set(this.unreadCount() + 1);
        }

        if (isNew || (notification.group_count ?? 1) > 1) {
            this.newNotification$.next(notification);
            this.maybeShowToast(notification);
            this.maybeShowBrowserNotification(notification);
        }
    }

    private applyRead(id: number): void {
        const current = this.notifications();
        const target = current.find((n) => n.id === id);

        if (target && !target.is_read) {
            this.unreadCount.set(Math.max(0, this.unreadCount() - 1));
        }

        this.notifications.set(current.map((n) =>
            n.id === id ? { ...n, is_read: true, read_at: n.read_at ?? new Date().toISOString() } : n
        ));
    }

    private applyAllRead(): void {
        this.notifications.set(this.notifications().map((n) => ({
            ...n,
            is_read: true,
            read_at: n.read_at ?? new Date().toISOString()
        })));
        this.unreadCount.set(0);
    }
}
